<template>
  <div class="h-full flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-dark-800 border border-red-200 dark:border-red-800 shadow-sm">
    <!-- Header -->
    <div class="p-4 bg-gradient-to-r from-red-500/10 to-orange-500/10 dark:from-red-500/20 dark:to-orange-500/20 border-b border-red-200/50 dark:border-red-800/30">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center text-white shadow-lg">
            <i class="fas fa-receipt"></i>
          </div>
          <div>
            <h3 class="text-sm font-bold text-gray-900 dark:text-white">
              Expenses
            </h3>
            <p class="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <span>{{ expenseDetails?.length || 0 }} transactions</span>
              <span
                v-if="pendingApprovals > 0"
                class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
              >
                <i class="fas fa-clock mr-1"></i>{{ pendingApprovals }}
              </span>
            </p>
          </div>
        </div>
        <div class="text-right">
          <p class="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Total Spent</p>
          <p class="text-lg font-black bg-gradient-to-r from-red-600 to-orange-600 dark:from-red-400 dark:to-orange-400 bg-clip-text text-transparent">
            {{ formatCurrency(expenseDetails?.reduce((sum, e) => sum + Number(e.total_cost), 0) || 0) }}
          </p>
        </div>
      </div>

      <!-- Breakdown Stats -->
      <div v-if="canViewBreakdown" class="grid grid-cols-2 gap-2">
        <div class="bg-white dark:bg-dark-700 p-2 rounded-lg text-center border border-red-200 dark:border-red-800">
          <p class="text-[10px] text-gray-600 dark:text-gray-400 mb-1">Cash</p>
          <p class="text-xs font-bold text-red-600 dark:text-red-400">
            {{ formatCurrency(expenseDetails?.reduce((sum, e) => e.payment_method === 'cash' ? sum + Number(e.total_cost) : sum, 0) || 0) }}
          </p>
        </div>
        <div class="bg-white dark:bg-dark-700 p-2 rounded-lg text-center border border-orange-200 dark:border-orange-800">
          <p class="text-[10px] text-gray-600 dark:text-gray-400 mb-1">Online</p>
          <p class="text-xs font-bold text-orange-600 dark:text-orange-400">
            {{ formatCurrency(expenseDetails?.reduce((sum, e) => e.payment_method === 'online' ? sum + Number(e.total_cost) : sum, 0) || 0) }}
          </p>
        </div>
      </div>
    </div>
    <!-- Content -->
    <div class="flex-1 overflow-hidden p-3">
      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center h-full">
        <div class="relative">
          <div class="w-10 h-10 rounded-full border-4 border-red-200 dark:border-red-800 border-t-red-600 dark:border-t-red-400 animate-spin"></div>
          <i class="fas fa-receipt absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-red-600 dark:text-red-400 text-sm"></i>
        </div>
        <p class="mt-2 text-xs font-medium text-gray-600 dark:text-gray-400">Loading expenses...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!expenseDetails || expenseDetails.length === 0" class="flex flex-col items-center justify-center h-full text-center">
        <div class="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-3">
          <i class="fas fa-inbox text-2xl text-red-600 dark:text-red-400"></i>
        </div>
        <p class="text-xs font-semibold text-gray-900 dark:text-white">No expenses found</p>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">No expenses for this date</p>
      </div>

      <!-- Expenses List -->
      <div v-else class="h-full overflow-y-auto custom-scrollbar space-y-1.5">
        <div v-for="expense in expenseDetails" :key="expense.expense_id" 
             class="group p-2.5 rounded-lg bg-white/50 dark:bg-dark-700/50 border border-red-200/30 dark:border-red-800/20 hover:border-red-300 dark:hover:border-red-700 hover:shadow-md transition-all duration-200 cursor-pointer"
             @click="openExpense(expense.expense_id)">
          <div class="flex items-center justify-between gap-2 mb-1.5">
            <div class="flex-1 min-w-0">
              <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 group-hover:underline truncate">
                {{ expense.supplier_name }}
              </p>
            </div>
            <div class="text-xs font-bold text-gray-900 dark:text-white whitespace-nowrap">
              {{ formatCurrency(expense.total_cost) }}
            </div>
          </div>
          <div>
            <span :class="getMethodClass(expense.payment_method)" class="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold">
              <i :class="getMethodIcon(expense.payment_method)" class="mr-1"></i>
              {{ formatPaymentMethod(expense.payment_method) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatCurrency } from '@/utils/formatters';
import { useAuth } from '@/composables/useAuth';

const { hasRole } = useAuth();

// Check if user can view breakdown (super_admin, admin, or manager)
const canViewBreakdown = computed(() => hasRole(['super_admin', 'admin', 'manager']));

const props = defineProps({
  expenseDetails: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  pendingApprovals: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['open-expense']);

const openExpense = (expenseId) => {
  emit('open-expense', expenseId);
};

const truncateText = (text, length = 15) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const getMethodClass = (method) => {
  if (!method) return 'bg-gray-100 text-gray-800';
  
  switch(method.toLowerCase()) {
    case 'cash':
      return 'bg-green-100 text-green-800';
    case 'online':
    case 'bank_transfer':
      return 'bg-blue-100 text-blue-800';
    case 'check':
      return 'bg-purple-100 text-purple-800';
    case 'credit_card':
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatPaymentMethod = (method) => {
  if (!method) return 'Unknown';
  
  // Replace underscores with spaces and capitalize each word
  return method.toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getMethodIcon = (method) => {
  switch (method?.toLowerCase()) {
    case 'cash':
      return 'fas fa-money-bill-wave';
    case 'online':
    case 'bank_transfer':
      return 'fas fa-university';
    case 'check':
      return 'fas fa-money-check-alt';
    case 'credit_card':
      return 'fas fa-credit-card';
    default:
      return 'fas fa-wallet';
  }
};
</script> 